/**
 * Shared pack/export helpers: status, blockers, metadata, filenames, grain text.
 */
import type { Fields, PackMetadata, Part } from '@/domain/schemas';
import { criticalBlocker } from '@/domain/fields';
import { GENERATOR_VERSION } from '@/domain/build';
import { isProvisional, isSelfAuthored, type ConstructionProfile } from '@/domain/profiles';
import { shortHash } from '@/domain/hash';
import { fmtMm } from '@/domain/units';
import type { BoardAssembly } from '@/families/assembly';

/**
 * What every export reads: the family-agnostic BoardAssembly, plus the construction (joint)
 * profile it was generated with and the design's appearance (for the name).
 */
export type ExportBuild = BoardAssembly & {
  construction: ConstructionProfile;
  design: { appearance?: { name?: string } };
  thickness?: { carcass: number; back: number };
};

/** Every pack carries this while the family's construction rules are self-authored (plan §5). */
export const WATERMARK = 'Construction rules self-authored, not maker-reviewed';
/** The first slice's watermark, for a provisional profile that is not marked self-authored. */
export const PROVISIONAL_WATERMARK = 'Provisional profile — check with your maker';

/** The watermark for a construction profile, or null once a maker has reviewed it. */
export const watermarkFor = (c: ConstructionProfile): string | null => (isSelfAuthored(c) ? WATERMARK : isProvisional(c) ? PROVISIONAL_WATERMARK : null);
export const INVALID_STAMP = 'Invalid — not for cutting';

/** Thrown when a CSV or print is requested for an invalid or unconfirmed design without {force:true}. */
export class ExportRefusedError extends Error {
  readonly reason: string;
  constructor(reason: string) {
    super(`Export refused: ${reason}`);
    this.name = 'ExportRefusedError';
    this.reason = reason;
  }
}

/**
 * Why CSV and print are off, or null. Unconfirmed criticals first ("Confirm width in Check
 * first."), then validity ("Invalid — not for cutting. <first error>"). JSON stays allowed.
 */
export function exportBlocker(build: Pick<BoardAssembly, 'validation'>, fields: Fields = {}): string | null {
  const pending = criticalBlocker(fields);
  if (pending) return pending;
  if (!build.validation.ok) return `${INVALID_STAMP}. ${build.validation.errors[0]?.message ?? ''}`.trim();
  return null;
}

export interface MetaInput {
  revisionId?: string | null;
  generatedAt?: string;
}

/** PackMetadata (test-plan §1): CSV metadata rows, JSON top-level fields and the print header. */
export function packMetadata(build: ExportBuild, { revisionId = null, generatedAt = new Date().toISOString() }: MetaInput = {}): PackMetadata {
  const c = build.construction;
  const carcass = build.stocks[0];
  return {
    revisionId,
    manufacturingHash: build.manufacturingHash,
    appearanceHash: build.appearanceHash,
    status: build.validation.ok ? 'draft' : 'invalid',
    provisionalProfile: isProvisional(c),
    generatedAt,
    stockId: carcass?.id ?? '',
    measuredThicknessMm: (carcass?.thicknessTicks ?? 0) / 10,
    profileId: c.id,
    profileVersion: c.version,
    profileReviewer: c.reviewer,
    profileReviewedAt: c.reviewedAt,
    generatorVersion: GENERATOR_VERSION,
    familyId: build.familyId,
    familyVersion: build.familyVersion,
    jointsProfileId: c.id,
    jointsVersion: c.version,
    selfAuthored: isSelfAuthored(c),
  };
}

/** "grid-bookcase-joints v1.0 · self-authored, not maker-reviewed" or "… · reviewed by A. Maker, 2026-11-02". */
export function profileLine(build: Pick<ExportBuild, 'construction'>): string {
  const c = build.construction;
  if (!isProvisional(c)) return `${c.id} v${c.version} · reviewed by ${c.reviewer}, ${c.reviewedAt}`;
  return isSelfAuthored(c) ? `${c.id} v${c.version} · self-authored, not maker-reviewed` : `${c.id} v${c.version} · provisional — check with your maker`;
}

/** URL- and filesystem-safe slug of a design name. */
export function slugify(name: string | null | undefined): string {
  const s = String(name ?? '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return s || 'bookcase';
}

/** `<name>-rN-<hash8>.<ext>`: every exported file names its revision and manufacturing hash. */
export function packFilename({ name, revisionId, manufacturingHash, ext }: { name?: string | null; revisionId: string | null; manufacturingHash: string; ext: string }): string {
  return `${slugify(name)}-${revisionId || 'draft'}-${shortHash(manufacturingHash)}.${ext.replace(/^\./, '')}`;
}

export const grainText = (p: Pick<Part, 'grainWorld'>): string => (p.grainWorld === 'y' ? 'Vertical' : p.grainWorld === 'z' ? 'Front to back' : 'Across');
export const grainArrow = (p: Pick<Part, 'grainWorld'>): string => (p.grainWorld === 'y' ? '↕' : p.grainWorld === 'z' ? '⤢' : '↔');

/** "Shelf 2: 900 × 300 × 25, glued up from 2 strips of 150" (plan §5). */
export function glueUpText(p: Pick<Part, 'label' | 'finished' | 'glueUp'>): string | null {
  if (!p.glueUp) return null;
  const f = (v: number) => fmtMm(v);
  return `${p.label}: ${f(p.finished.l)} × ${f(p.finished.w)} × ${f(p.finished.t)}, glued up from ${p.glueUp.strips} strips of ${f(p.glueUp.stripWidthTicks)}`;
}

const dateFmt = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
/** "9 October 2026, 14:02" (local time; date and time formatted separately so ICU versions agree). */
export function formatPrinted(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : `${dateFmt.format(d)}, ${timeFmt.format(d)}`;
}

/** Escape text for HTML/SVG content and attributes. */
export function esc(v: unknown): string {
  return String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
